import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';

// In-app replacement for window.confirm. The native dialog is silently
// suppressed in some embedded browsers (in-app browsers on phones, the Claude
// desktop preview), where it returns false and the button appears dead.
// Usage: const confirm = useConfirm(); if (!(await confirm('Usunąć?'))) return;
const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((message, options = {}) => new Promise(resolve => {
    resolver.current = resolve;
    setRequest({ message, ...options });
  }), []);

  const close = (result) => {
    resolver.current?.(result);
    resolver.current = null;
    setRequest(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!request} onOpenChange={open => !open && close(false)}>
        <AlertDialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-sm" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="heading-text text-xl text-white">
              {request?.title || 'Na pewno?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1A1]">{request?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              onClick={() => close(false)}
              className="rounded-full bg-transparent border-[#2A2A2A] text-white hover:bg-[#2A2A2A]"
              data-testid="confirm-cancel"
            >
              {request?.cancelLabel || 'Anuluj'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              className="rounded-full bg-[#1E1E1E] text-white border border-[#C9A84C] hover:bg-[#2A2A2A]"
              data-testid="confirm-ok"
            >
              {request?.confirmLabel || 'Tak'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
